import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Grid, CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Card, CardContent, Tabs, Tab, Alert, Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  AccountBalance as BankIcon,
  CheckCircle as ReconcileIcon,
  Block as IgnoreIcon,
  Search as SearchIcon,
  Upload as ImportIcon,
  Lightbulb as SuggestionIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BankReconciliation() {
  const [tab, setTab] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [openNewAccount, setOpenNewAccount] = useState(false);
  const [openReconcile, setOpenReconcile] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [filters, setFilters] = useState({ status: 'pending' });

  const [accountForm, setAccountForm] = useState({
    name: '',
    bank_name: '',
    bank_code: '',
    agency: '',
    account_number: '',
    account_type: 'checking',
    initial_balance: 0
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchTransactions();
      fetchSummary();
    }
  }, [selectedAccount, filters]);

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/bank/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data || []);
      if (response.data?.length > 0 && !selectedAccount) {
        setSelectedAccount(response.data[0].id);
      }
    } catch (error) {
      toast.error('Erro ao carregar contas');
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        bank_account_id: selectedAccount,
        status: filters.status
      });
      const response = await axios.get(`${API}/api/bank/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.data || []);
    } catch (error) {
      toast.error('Erro ao carregar transações');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/bank/summary?bank_account_id=${selectedAccount}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Erro ao carregar resumo');
    }
  };

  const fetchSuggestions = async (transaction) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/bank/transactions/${transaction.id}/suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Erro ao buscar sugestões');
    }
  };

  const handleCreateAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/bank/accounts`, accountForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Conta criada com sucesso!');
      setOpenNewAccount(false);
      fetchAccounts();
      setAccountForm({
        name: '',
        bank_name: '',
        bank_code: '',
        agency: '',
        account_number: '',
        account_type: 'checking',
        initial_balance: 0
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao criar conta');
    }
  };

  const handleReconcile = async (suggestion) => {
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      if (suggestion.type === 'payable') payload.payable_id = suggestion.id;
      if (suggestion.type === 'receivable') payload.receivable_id = suggestion.id;
      if (suggestion.type === 'sale') payload.sale_id = suggestion.id;

      await axios.post(`${API}/api/bank/transactions/${selectedTransaction.id}/reconcile`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transação conciliada!');
      setOpenReconcile(false);
      setSelectedTransaction(null);
      fetchTransactions();
      fetchSummary();
    } catch (error) {
      toast.error('Erro ao conciliar');
    }
  };

  const handleIgnore = async (transaction) => {
    if (!window.confirm('Ignorar esta transação?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/bank/transactions/${transaction.id}/ignore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transação ignorada');
      fetchTransactions();
      fetchSummary();
    } catch (error) {
      toast.error('Erro ao ignorar');
    }
  };

  const openReconcileDialog = (transaction) => {
    setSelectedTransaction(transaction);
    fetchSuggestions(transaction);
    setOpenReconcile(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          <BankIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Conciliação Bancária
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenNewAccount(true)}
        >
          Nova Conta
        </Button>
      </Box>

      {/* Seleção de conta e resumo */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Conta Bancária</InputLabel>
            <Select
              value={selectedAccount}
              label="Conta Bancária"
              onChange={(e) => setSelectedAccount(e.target.value)}
            >
              <MenuItem value="">
                <em>Selecione uma conta</em>
              </MenuItem>
              {accounts.map((acc) => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.name} - {acc.bank_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {summary && (
          <>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption">Pendentes</Typography>
                  <Typography variant="h6" color="warning.main">{summary.pending_count}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption">Conciliadas</Typography>
                  <Typography variant="h6" color="success.main">{summary.reconciled_count}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption">Créditos Pend.</Typography>
                  <Typography variant="h6" color="success.main">{formatCurrency(summary.pending_credits)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption">Débitos Pend.</Typography>
                  <Typography variant="h6" color="error.main">{formatCurrency(summary.pending_debits)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Filtros */}
      <Paper sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="pending">Pendentes</MenuItem>
                <MenuItem value="reconciled">Conciliadas</MenuItem>
                <MenuItem value="ignored">Ignoradas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela de transações */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Vinculado a</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhuma transação encontrada
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((trans) => (
                <TableRow key={trans.id}>
                  <TableCell>
                    {new Date(trans.transaction_date).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={trans.type === 'credit' ? 'Crédito' : 'Débito'}
                      color={trans.type === 'credit' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{trans.description}</TableCell>
                  <TableCell align="right" sx={{ color: trans.type === 'credit' ? 'success.main' : 'error.main' }}>
                    {trans.type === 'credit' ? '+' : '-'} {formatCurrency(trans.amount)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={trans.status === 'pending' ? 'Pendente' : trans.status === 'reconciled' ? 'Conciliada' : 'Ignorada'}
                      color={trans.status === 'reconciled' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {trans.payable && `Conta a Pagar #${trans.payable_id}`}
                    {trans.receivable && `Conta a Receber #${trans.receivable_id}`}
                    {trans.sale && `Venda #${trans.sale_id}`}
                  </TableCell>
                  <TableCell>
                    {trans.status === 'pending' && (
                      <>
                        <Tooltip title="Conciliar">
                          <IconButton size="small" color="primary" onClick={() => openReconcileDialog(trans)}>
                            <ReconcileIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ignorar">
                          <IconButton size="small" onClick={() => handleIgnore(trans)}>
                            <IgnoreIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Nova Conta */}
      <Dialog open={openNewAccount} onClose={() => setOpenNewAccount(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Conta Bancária</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome da Conta"
                value={accountForm.name}
                onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="Banco"
                value={accountForm.bank_name}
                onChange={(e) => setAccountForm({ ...accountForm, bank_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Código"
                value={accountForm.bank_code}
                onChange={(e) => setAccountForm({ ...accountForm, bank_code: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Agência"
                value={accountForm.agency}
                onChange={(e) => setAccountForm({ ...accountForm, agency: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Conta"
                value={accountForm.account_number}
                onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={accountForm.account_type}
                  label="Tipo"
                  onChange={(e) => setAccountForm({ ...accountForm, account_type: e.target.value })}
                >
                  <MenuItem value="checking">Conta Corrente</MenuItem>
                  <MenuItem value="savings">Poupança</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Saldo Inicial"
                type="number"
                value={accountForm.initial_balance}
                onChange={(e) => setAccountForm({ ...accountForm, initial_balance: parseFloat(e.target.value) })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewAccount(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateAccount}>Criar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Conciliar */}
      <Dialog open={openReconcile} onClose={() => setOpenReconcile(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Conciliar Transação</DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info">
                <strong>{selectedTransaction.type === 'credit' ? 'Crédito' : 'Débito'}:</strong> {formatCurrency(selectedTransaction.amount)}<br />
                <strong>Descrição:</strong> {selectedTransaction.description}
              </Alert>
            </Box>
          )}

          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            <SuggestionIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Sugestões de conciliação:
          </Typography>

          {suggestions.length === 0 ? (
            <Typography color="textSecondary">Nenhuma sugestão encontrada</Typography>
          ) : (
            suggestions.map((sug, index) => (
              <Paper key={index} sx={{ p: 2, mb: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => handleReconcile(sug)}>
                <Grid container justifyContent="space-between" alignItems="center">
                  <Grid item>
                    <Typography variant="subtitle2">
                      {sug.type === 'payable' ? 'Conta a Pagar' : sug.type === 'receivable' ? 'Conta a Receber' : 'Venda'}
                    </Typography>
                    <Typography variant="body2">{sug.description}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Vencimento: {sug.due_date ? new Date(sug.due_date).toLocaleDateString('pt-BR') : '-'}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="h6">{formatCurrency(sug.amount)}</Typography>
                    <Chip label={`${sug.match_score}% match`} size="small" color={sug.match_score > 80 ? 'success' : 'default'} />
                  </Grid>
                </Grid>
              </Paper>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReconcile(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
